import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, Control, UseFormSetValue } from 'react-hook-form';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { Grid } from '@mui/material';
import FormInput from '../../../../components/FormInput';
import { CustomerAddressBase } from '../types';

interface Props {
  control: Control<CustomerAddressBase, Object>;
  setValue: UseFormSetValue<CustomerAddressBase>;
  onLocationChange: (lat: number, lng: number) => void;
}

const mapContainerStyle = {
  width: '100%',
  height: '200px',
  position: 'relative' as 'relative',
};

const inputStyle: React.CSSProperties = {
  top: '0',
  bottom: '0',
  marginBottom: '16px',
  paddingTop: '16px',
  left: '0',
  zIndex: 10,
  width: '100%',
  padding: '16.5px 14px',
  fontSize: '16px',
  height: '40px',
  border: 'none',
  borderBottom: '1px solid #000',
  borderBottomColor: '#000',
  borderRadius: '0',
  backgroundColor: 'rgb(245, 248, 250)',
  transition: 'border-color 0.2s ease-in-out',
  outline: 'none',
  borderTopLeftRadius: '4px',
  borderTopRightRadius: '4px',
};

const defaultCenter = {
  lat: 41.0082,
  lng: 28.9784,
};

function AddModalForm({ control, setValue, onLocationChange }: Props) {
  const { t } = useTranslation('customerAddress');
  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

  // Initialize Google Maps API Loader
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: apiKey || '',
    libraries: ['places', 'geometry'], // Ensure the libraries array is consistent
  });
  const [center, setCenter] = useState(defaultCenter);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isLoaded && inputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
      );

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry && place.geometry.location) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          setCenter({ lat, lng });
          onLocationChange(lat, lng);
          if (place.formatted_address) {
            setValue('address', place.formatted_address);
          }
        }
      });
    }
  }, [isLoaded, onLocationChange, setValue]);

  const fetchAddress = async (lat: number, lng: number) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`,
      );
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        const address = data.results[0].formatted_address;
        if (inputRef.current) {
          inputRef.current.value = address;
          setValue('address', address);
        }
      }
    } catch (error) {
      console.error('Error fetching address:', error);
    }
  };

  const handleMarkerDragEnd = (event: google.maps.MapMouseEvent) => {
    const lat = event.latLng?.lat();
    const lng = event.latLng?.lng();
    if (lat && lng) {
      setCenter({ lat, lng });
      onLocationChange(lat, lng);
      fetchAddress(lat, lng);
    }
  };

  return (
    <>
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <FormInput
            label={t('customerAddress.addModal.form.name.label')}
            error={fieldState.invalid}
            helperText={
              fieldState.error ? t(fieldState.error.message as any) : undefined
            }
            {...field}
          />
        )}
      />

      <div style={mapContainerStyle}>
        <div>{t('customerAddress.addModal.form.address.label')}</div>
        <Controller
          name="address"
          control={control}
          render={({ field }) => (
            <input
              id="origin_address_search"
              type="text"
              placeholder={t('customerAddress.addModal.form.address.label')}
              style={inputStyle}
              onChange={field.onChange}
              ref={inputRef}
            />
          )}
        />

        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={center}
            zoom={18}
            options={{
              disableDefaultUI: true,
              zoomControl: true,
              gestureHandling: 'greedy',
            }}
          >
            <Marker
              position={center}
              draggable
              onDragEnd={handleMarkerDragEnd}
            />
          </GoogleMap>
        ) : (
          <div>Loading map...</div>
        )}
      </div>

      <Grid container spacing={2} style={{ marginTop: '80px' }}>
        <Grid item xs={4}>
          <Controller
            name="apartment_number"
            control={control}
            render={({ field, fieldState }) => (
              <FormInput
                error={fieldState.invalid}
                helperText={
                  fieldState.error
                    ? t(fieldState.error.message as any)
                    : undefined
                }
                {...field}
                label="Bina Adı/No"
              />
            )}
          />
        </Grid>
        <Grid item xs={4}>
          <Controller
            name="floor"
            control={control}
            render={({ field, fieldState }) => (
              <FormInput
                error={fieldState.invalid}
                helperText={
                  fieldState.error
                    ? t(fieldState.error.message as any)
                    : undefined
                }
                {...field}
                label="Kat"
              />
            )}
          />
        </Grid>
        <Grid item xs={4}>
          <Controller
            name="door_number"
            control={control}
            render={({ field, fieldState }) => (
              <FormInput
                error={fieldState.invalid}
                helperText={
                  fieldState.error
                    ? t(fieldState.error.message as any)
                    : undefined
                }
                {...field}
                label="Daire No"
              />
            )}
          />
        </Grid>
      </Grid>

      <Controller
        name="note"
        control={control}
        render={({ field, fieldState }) => (
          <FormInput
            sx={{ marginTop: 2 }}
            label={t('customerAddress.addModal.form.note.label')}
            multiline
            rows={5}
            error={fieldState.invalid}
            helperText={
              fieldState.error ? t(fieldState.error.message as any) : undefined
            }
            {...field}
          />
        )}
      />
    </>
  );
}

export default AddModalForm;
